import { moneyInputError, parseMoney } from '@/shared/lib/format';

import type {
  ExposureKind,
  SimulationExposure,
  SimulationExposureDimension,
} from './types';

/**
 * Exposicao ficticia do tomador no simulador (ADR 0088, secao 10).
 *
 * E o mesmo campo do retrato da execucao real, que ali vem da carteira: o disponivel do
 * tomador no grupo de credito e o acumulo do tomador e do grupo economico, sem a propria
 * operacao. Cada valor informado vira uma dimensao calculavel; o valor em branco nao vai,
 * e a regra que dependeria dele segue como sem exposicao (analise manual). Funcoes puras,
 * testadas sem tela; o dinheiro sai de `parseMoney`, sem ponto flutuante.
 */

/** Identificador do grupo economico ficticio. O motor so precisa de um. */
export const GRUPO_FICTICIO = 'grupo-ficticio';

export interface EstadoDaExposicao {
  ligada: boolean;
  grupoIdentificado: boolean;
  ccgFormalizada: boolean;
  disponivel: string;
  acumuloTomadorNoGrupo: string;
  acumuloTomador: string;
  acumuloGrupoNoGrupo: string;
  acumuloGrupo: string;
}

export type CampoDeValor =
  | 'disponivel'
  | 'acumuloTomadorNoGrupo'
  | 'acumuloTomador'
  | 'acumuloGrupoNoGrupo'
  | 'acumuloGrupo';

export interface CampoDaExposicao {
  campo: CampoDeValor;
  kind: ExposureKind;
  rotulo: string;
  ajuda: string;
  /** So vale com grupo economico identificado. */
  doGrupo: boolean;
  /** Sem grupo economico, e o que as regras leem (o tomador isolado). */
  doTomadorIsolado: boolean;
}

export const CAMPOS_DA_EXPOSICAO: CampoDaExposicao[] = [
  {
    campo: 'disponivel',
    kind: 'limite_do_tomador',
    rotulo: 'Limite disponivel do tomador no grupo (R$)',
    ajuda: 'LIM-008 compara com a IS.',
    doGrupo: false,
    doTomadorIsolado: false,
  },
  {
    campo: 'acumuloTomadorNoGrupo',
    kind: 'acumulo_do_tomador_no_grupo',
    rotulo: 'Acumulo do tomador no grupo de credito (R$)',
    ajuda: 'LIM-004 e LIM-002 sem grupo economico.',
    doGrupo: false,
    doTomadorIsolado: true,
  },
  {
    campo: 'acumuloTomador',
    kind: 'acumulo_do_tomador',
    rotulo: 'Acumulo do tomador em todos os grupos (R$)',
    ajuda: 'LIM-001 e LIM-011 sem grupo economico.',
    doGrupo: false,
    doTomadorIsolado: true,
  },
  {
    campo: 'acumuloGrupoNoGrupo',
    kind: 'acumulo_do_grupo_economico_no_grupo',
    rotulo: 'Acumulo do grupo economico no grupo de credito (R$)',
    ajuda: 'LIM-004 e LIM-002.',
    doGrupo: true,
    doTomadorIsolado: false,
  },
  {
    campo: 'acumuloGrupo',
    kind: 'acumulo_do_grupo_economico',
    rotulo: 'Acumulo do grupo economico em todos os grupos (R$)',
    ajuda: 'LIM-001 e LIM-011.',
    doGrupo: true,
    doTomadorIsolado: false,
  },
];

export function exposicaoInicial(): EstadoDaExposicao {
  return {
    ligada: false,
    grupoIdentificado: false,
    ccgFormalizada: false,
    disponivel: '',
    acumuloTomadorNoGrupo: '',
    acumuloTomador: '',
    acumuloGrupoNoGrupo: '',
    acumuloGrupo: '',
  };
}

/** O campo vale para a situacao do grupo economico escolhida. */
export function campoAtivo(
  campo: CampoDaExposicao,
  estado: EstadoDaExposicao,
): boolean {
  return !campo.doGrupo || estado.grupoIdentificado;
}

export interface ExposicaoMontada {
  exposicao?: SimulationExposure;
  /** Chave `exposicao.<campo>`. */
  erros: Record<string, string>;
}

/** Monta a exposicao do pedido, com o erro de cada valor que nao pode seguir. */
export function montarExposicao(estado: EstadoDaExposicao): ExposicaoMontada {
  const erros: Record<string, string> = {};
  if (!estado.ligada) return { erros };

  const dimensions: SimulationExposureDimension[] = [];
  for (const campo of CAMPOS_DA_EXPOSICAO) {
    if (!campoAtivo(campo, estado)) continue;
    const texto = estado[campo.campo].trim();
    if (texto === '') continue;
    const valor = parseMoney(texto);
    if (!valor) {
      erros[`exposicao.${campo.campo}`] = moneyInputError(texto) ?? 'Valor invalido.';
      continue;
    }
    if (valor.amount < 0) {
      erros[`exposicao.${campo.campo}`] = 'Use um valor maior ou igual a zero.';
      continue;
    }
    const dimensao: SimulationExposureDimension = {
      kind: campo.kind,
      situation: 'calculavel',
    };
    if (campo.kind === 'limite_do_tomador') dimensao.available = valor;
    else dimensao.accumulated = valor;
    if (campo.doGrupo) {
      dimensao.economic_group_id = GRUPO_FICTICIO;
      dimensao.economic_group_name = 'Grupo economico ficticio';
    }
    dimensions.push(dimensao);
  }
  if (Object.keys(erros).length > 0) return { erros };
  return {
    erros,
    exposicao: {
      economic_group_identified: estado.grupoIdentificado,
      ccg_formalized: estado.ccgFormalizada,
      dimensions,
    },
  };
}
